import { useEffect, useState } from "react";
import AccountButton from "../AccountButton";
import AccountInput from "../AccountInput";
import AccountSection from "../AccountSection";
import AccountTextarea from "../AccountTextarea";
import { t } from "../i18n";
import { replaceNullWithPlaceholder } from "../utils";
import { useSubscriptionAccess } from "../context/SubscriptionAccessContext";

const STORAGE_KEY = "patient_documents";

export default function DocumentsPage({ lang = "en", goToTab }) {
  const { dontHaveAccess } = useSubscriptionAccess();
  const [search, setSearch] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileBase64, setFileBase64] = useState(null);
  const [form, setForm] = useState({
    category: "",
    title: "",
    details: "",
  });
  const [fileInfo, setFileInfo] = useState({
    type: "",
    size: "",
    created: "",
    updated: "",
  });
  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load records from localStorage on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    loadRecordsFromStorage();
  }, []);

  // Load records from localStorage
  const loadRecordsFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedRecords = JSON.parse(stored);
        setRecords(parsedRecords);
      }
    } catch (error) {
      console.error("Error loading records from storage:", error);
    }
  };

  // Save records to localStorage
  const saveRecordsToStorage = (recordsToSave) => {
    try {
      setIsSaving(true);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(recordsToSave));
    } catch (error) {
      console.error("Error saving records to storage:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Dropdown options
  const categoryOptions = [
    { value: "", label: t("choose", lang) },
    { value: "lab", label: t("labResults", lang) },
    { value: "imaging", label: t("imaging", lang) },
    { value: "prescription", label: t("prescriptions", lang) },
    { value: "other", label: t("other", lang) },
  ];
  const statusOptions = [
    { value: "all", label: t("allStatuses", lang) },
    { value: "active", label: t("active", lang) },
    { value: "inactive", label: t("inactive", lang) },
  ];

  // Handlers
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleStatusChange = (e) => setStatusFilter(e.target.value);
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    // Clear any previous errors
    setError("");
    if (file) {
      // Convert file to base64 for storage
      const reader = new FileReader();
      reader.onload = (event) => {
        setFileBase64(event.target.result);
        setError(""); // Clear error on success
      };
      reader.onerror = () => {
        console.error("Error reading file");
        setError("Error reading file. Please try again.");
        setSelectedFile(null);
        setFileBase64(null);
      };
      reader.readAsDataURL(file);

      // Get file format from extension
      const fileName = file.name;
      const fileExtension = fileName.split(".").pop().toUpperCase();
      const format =
        fileExtension === "PDF"
          ? "PDF"
          : ["JPG", "JPEG", "PNG", "GIF", "WEBP"].includes(fileExtension)
            ? "IMAGE"
            : fileExtension;

      setFileInfo({
        type: file.type,
        size: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
        created: new Date().toLocaleDateString(),
        updated: new Date().toLocaleDateString(),
        format: format,
      });
    } else {
      setFileInfo({ type: "", size: "", created: "", updated: "", format: "" });
      setFileBase64(null);
    }
  };

  // Save document handler
  const handleSave = () => {
    // Clear previous messages
    setError("");
    setSuccessMessage("");

    // Validation
    if (!selectedFile) {
      setError(t("pleaseSelectFile", lang) || "Please select a file to upload");
      return;
    }
    if (!form.category) {
      setError(t("pleaseSelectCategory", lang) || "Please select a category");
      return;
    }
    if (!form.title || !form.title.trim()) {
      setError(t("pleaseEnterTitle", lang) || "Please enter a title");
      return;
    }

    // Get category label
    const categoryLabel =
      categoryOptions.find((opt) => opt.value === form.category)?.label ||
      form.category;

    // Create new record
    const newRecord = {
      id: Date.now().toString(), // Unique ID
      name: selectedFile.name,
      category: categoryLabel,
      format:
        fileInfo.format || selectedFile.name.split(".").pop().toUpperCase(),
      size: fileInfo.size,
      updated: new Date().toLocaleDateString(),
      created: new Date().toLocaleDateString(),
      status: "active",
      title: form.title,
      details: form.details,
      fileBase64: fileBase64,
      fileType: fileInfo.type,
    };

    // Add to records and save to localStorage
    const updatedRecords = [...records, newRecord];
    setRecords(updatedRecords);
    saveRecordsToStorage(updatedRecords);

    // Reset form
    setForm({ category: "", title: "", details: "" });
    setSelectedFile(null);
    setFileBase64(null);
    setFileInfo({ type: "", size: "", created: "", updated: "", format: "" });

    // Clear file input
    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.value = "";
    }

    setSuccessMessage(
      t("documentSaved", lang) || "Document saved successfully!",
    );

    // Clear success message after 3 seconds
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  // Delete document handler
  const handleDelete = (id) => {
    const document = records.find((rec) => rec.id === id);
    if (document) {
      setDocumentToDelete({ id, document });
      setShowDeleteConfirm(true);
    }
  };

  // Confirm delete document handler
  const confirmDeleteDocument = () => {
    if (!documentToDelete) return;

    try {
      setIsDeleting(true);
      setError("");
      setSuccessMessage("");

      const updatedRecords = records.filter(
        (rec) => rec.id !== documentToDelete.id,
      );
      setRecords(updatedRecords);
      saveRecordsToStorage(updatedRecords);
      setSuccessMessage(
        t("documentDeleted", lang) || "Document deleted successfully!",
      );

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting document:", error);
      setError(error.message || "Failed to delete document. Please try again.");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
    }
  };

  // Download document handler
  const handleDownload = (record) => {
    if (record.fileBase64) {
      const link = document.createElement("a");
      link.href = record.fileBase64;
      link.download = record.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Filtered records
  const filteredRecords = records.filter((rec) => {
    const matchesSearch =
      rec.name?.toLowerCase().includes(search.toLowerCase()) ||
      rec.title?.toLowerCase().includes(search.toLowerCase()) ||
      rec.category?.toLowerCase().includes(search.toLowerCase()) ||
      rec.format?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || rec.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center pb-8 px-4 md:px-0">
      <div className="w-full max-w-4xl">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-red-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-green-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-green-700 text-sm">{successMessage}</span>
              </div>
            </div>
          )}

          {/* File Upload */}
          <AccountSection>{t("fileUpload", lang)}</AccountSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Image/File Placeholder */}
            <div className="flex flex-col items-center justify-center bg-[#f4f5ff] rounded-lg border border-[#e5e7eb] min-h-[300px] min-w-[300px] max-w-[350px] w-full aspect-square relative">
              {selectedFile ? (
                <>
                  {selectedFile.type.startsWith("image/") ? (
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="preview"
                      className="object-contain max-h-[250px] max-w-[250px]"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center p-4">
                      <svg
                        className="w-24 h-24 text-[#4962d8] mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-sm font-semibold text-[#2d348f] mt-2">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {fileInfo.format || "FILE"}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      setFileBase64(null);
                      setFileInfo({
                        type: "",
                        size: "",
                        created: "",
                        updated: "",
                        format: "",
                      });
                      const fileInput = document.getElementById("file-upload");
                      if (fileInput) fileInput.value = "";
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                    title="Remove file"
                  >
                    ×
                  </button>
                </>
              ) : (
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center justify-center w-full h-full cursor-pointer"
                >
                  <svg
                    className="w-16 h-16 text-[#bfc6e0] mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <span className="text-lg font-semibold text-[#bfc6e0]">
                    {t("clickToUpload", lang) || "Click to Upload"}
                  </span>
                  <span className="text-sm text-[#bfc6e0] mt-2">
                    {t("orDragAndDrop", lang) || "or drag and drop"}
                  </span>
                  <input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                    disabled={dontHaveAccess}
                  />
                </label>
              )}
            </div>
            {/* Form Fields */}
            <div className="flex flex-col gap-2">
              <div className="mb-3 w-full">
                <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                  {t("categoryFolder", lang)}
                </label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleFormChange}
                  disabled={dontHaveAccess}
                  className="border-0 border-b-2 w-full border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                >
                  {categoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <AccountInput
                label={t("title", lang)}
                name="title"
                value={form.title}
                onChange={handleFormChange}
                disabled={dontHaveAccess}
              />
              <AccountTextarea
                label={t("detailsNotes", lang)}
                name="details"
                value={form.details}
                onChange={handleFormChange}
                disabled={dontHaveAccess}
                rows={5}
              />
              {/* File Info */}
              <div className="text-xs text-[#837f90] mt-2">
                {selectedFile?.name && (
                  <div>
                    {t("file", lang)}:
                    {replaceNullWithPlaceholder(selectedFile.name)}
                  </div>
                )}

                <>
                  {fileInfo.type && (
                    <div>
                      {t("type", lang)}:{" "}
                      {replaceNullWithPlaceholder(fileInfo.type)}
                    </div>
                  )}
                  {fileInfo.size && (
                    <div>
                      {t("size", lang)}:{" "}
                      {replaceNullWithPlaceholder(fileInfo.size)}
                    </div>
                  )}
                  {fileInfo.created && (
                    <div>
                      {t("created", lang)}:{" "}
                      {replaceNullWithPlaceholder(fileInfo.created)}
                    </div>
                  )}
                  {fileInfo.updated && (
                    <div>
                      {t("updated", lang)}:{" "}
                      {replaceNullWithPlaceholder(fileInfo.updated)}
                    </div>
                  )}
                </>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <AccountButton
              className="md:w-1/3"
              disabled={isSaving}
              onClick={() => goToTab("prev")}
            >
              <span className="mr-2">&laquo;</span> {t("back", lang)}
            </AccountButton>
            <AccountButton
              disabled={isSaving || dontHaveAccess}
              className="md:w-1/3"
              onClick={handleSave}
            >
              {isSaving ? "Saving..." : "Save"}
            </AccountButton>
            <AccountButton
              disabled={isSaving}
              className="md:w-1/3"
              onClick={() => goToTab("next")}
            >
              {t("next", lang)} <span className="ml-2">&raquo;</span>
            </AccountButton>
          </div>

          {/* My Records Table */}
          <AccountSection>{t("myRecords", lang)}</AccountSection>
          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
            <div className="flex-1 flex items-center gap-2">
              <input
                id="record-search"
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={t("searchPlaceholder", lang)}
                className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                style={{ maxWidth: 200 }}
              />
              <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-[#f7f8fa] border border-gray-200">
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="#837f90"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </span>
            </div>
            <select
              value={statusFilter}
              onChange={handleStatusChange}
              className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
              style={{ maxWidth: 140 }}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto max-w-xs sm:max-w-full">
            <table className="bg-white text-sm min-w-full">
              <thead>
                <tr className="bg-[#f7f8fa] text-[#837f90]">
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("documentName", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("category", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("format", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("size", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("updated", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("actions", lang)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-[#837f90]">
                      {t("noRecordsFound", lang)}
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((rec) => (
                    <tr
                      key={rec.id}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-4 py-2">
                        {replaceNullWithPlaceholder(rec.name)}
                      </td>
                      <td className="px-4 py-2">
                        {replaceNullWithPlaceholder(rec.category)}
                      </td>
                      <td className="px-4 py-2">
                        {replaceNullWithPlaceholder(rec.format)}
                      </td>
                      <td className="px-4 py-2">
                        {replaceNullWithPlaceholder(rec.size)}
                      </td>
                      <td className="px-4 py-2">
                        {replaceNullWithPlaceholder(rec.updated)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block px-3 py-1 text-xs rounded-full ${
                              rec.status === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {t(rec.status, lang)}
                          </span>
                          <button
                            onClick={() => handleDownload(rec)}
                            className="text-[#4962d8] hover:text-[#2d348f] p-1"
                            title={t("download", lang) || "Download"}
                            disabled={dontHaveAccess}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(rec.id)}
                            disabled={dontHaveAccess}
                            className="text-red-500 hover:text-red-700 p-1"
                            title={t("delete", lang) || "Delete"}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t("deleteDocument", lang) || "Delete Document"}
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                {t("confirmDeleteDocument", lang) ||
                  "Are you sure you want to delete"}{" "}
                "
                {documentToDelete?.document?.name ||
                  documentToDelete?.document?.title}
                "?{" "}
                {t("actionCannotBeUndone", lang) ||
                  "This action cannot be undone."}
              </p>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDocumentToDelete(null);
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  {t("cancel", lang) || "Cancel"}
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteDocument}
                  disabled={isDeleting}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isDeleting ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {t("deleting", lang) || "Deleting..."}
                    </div>
                  ) : (
                    t("delete", lang) || "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
